import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/utils/api';
import axios from 'axios';

const ROLE_COLORS = {
  admin: { bg: 'bg-slate-800', text: 'text-white', badge: 'bg-slate-700 text-slate-200' },
  reception: { bg: 'bg-blue-600', text: 'text-white', badge: 'bg-blue-100 text-blue-700' },
  atelier: { bg: 'bg-amber-600', text: 'text-white', badge: 'bg-amber-100 text-amber-700' },
};

const ROLE_LABELS = { admin: 'Admin', reception: 'Reception', atelier: 'Atelier' };

const GlobalChat = ({ auth }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  const senderName = auth?.admin ? `${auth.admin.first_name || ''} ${auth.admin.last_name || ''}`.trim() : 'Utilisateur';
  const senderRole = auth?.admin?.role || 'admin';

  const loadMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/messages?limit=50`);
      setMessages(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // WebSocket for real-time messages
  useEffect(() => {
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/notifications`;
    let reconnectTimer;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'global_chat_message') {
            const newMsg = {
              id: data.id,
              sender_name: data.sender_name,
              sender_role: data.sender_role,
              message: data.message,
              created_at: data.timestamp,
            };
            setMessages(prev => {
              if (prev.some(m => m.id === newMsg.id)) return prev;
              return [...prev, newMsg];
            });
            if (!open) setUnread(prev => prev + 1);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };

    connect();
    return () => { clearTimeout(reconnectTimer); wsRef.current?.close(); };
  }, [open]);

  const handleOpen = () => { setOpen(true); setUnread(0); };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/chat/messages`, {
        message: text,
        sender_name: senderName,
        sender_role: senderRole,
      });
      setInput('');
    } catch { /* ignore */ }
    finally { setSending(false); }
  };

  const fmtTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      const today = new Date();
      if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
      const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
      if (d.toDateString() === yesterday.toDateString()) return 'Hier';
      return d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  // Group messages by date
  let lastDate = '';

  if (!open) {
    return (
      <button onClick={handleOpen} data-testid="global-chat-toggle"
        className="fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-700 flex items-center justify-center transition-all hover:scale-105">
        <MessageCircle className="w-6 h-6" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="chat-unread-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96 h-[500px] flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden" data-testid="global-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          <span className="font-semibold text-sm">Chat inter-services</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700">{messages.length}</span>
        </div>
        <button onClick={() => setOpen(false)} className="hover:bg-slate-700 rounded p-1 transition-colors" data-testid="close-chat">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
        {messages.length === 0 && (
          <p className="text-center text-xs text-slate-400 py-8">Aucun message. Commencez la conversation !</p>
        )}
        {messages.map((msg) => {
          const isMe = msg.sender_name === senderName;
          const roleStyle = ROLE_COLORS[msg.sender_role] || ROLE_COLORS.admin;
          const msgDate = fmtDate(msg.created_at);
          let showDateHeader = false;
          if (msgDate !== lastDate) {
            lastDate = msgDate;
            showDateHeader = true;
          }

          return (
            <React.Fragment key={msg.id}>
              {showDateHeader && (
                <div className="flex items-center gap-2 py-1">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-[10px] text-slate-400 font-medium">{msgDate}</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
              )}
              <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${msg.id}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 ${isMe ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200'}`}>
                  {!isMe && (
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[11px] font-semibold">{msg.sender_name}</span>
                      <span className={`text-[9px] px-1 py-0.5 rounded-full ${roleStyle.badge}`}>
                        {ROLE_LABELS[msg.sender_role] || msg.sender_role}
                      </span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed">{msg.message}</p>
                  <p className={`text-[10px] mt-0.5 ${isMe ? 'text-slate-400' : 'text-slate-400'} text-right`}>{fmtTime(msg.created_at)}</p>
                </div>
              </div>
            </React.Fragment>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-slate-200 bg-white">
        <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Votre message..."
            className="flex-1 text-sm h-9"
            autoFocus
            data-testid="chat-input"
          />
          <Button type="submit" size="sm" className="h-9 px-3" disabled={!input.trim() || sending} data-testid="chat-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default GlobalChat;
